/**
 * NexusHub - Main container for CounselCore Nexus
 * Sub-views: Referral Queue, Capacity Board, Intake Form, Clinician Profiles, Inbox, Dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Inbox, Users, PlusCircle, UserCog, BarChart3, Mail, Globe
} from "lucide-react";
import NexusReferralQueue from './NexusReferralQueue';
import NexusCapacityBoard from './NexusCapacityBoard';
import NexusReferralIntake from './NexusReferralIntake';
import NexusClinicianProfile from './NexusClinicianProfile';
import NexusDashboard from './NexusDashboard';
import NexusClinicianInbox from './NexusClinicianInbox';
import NexusPlatformView from './NexusPlatformView';

const NexusHub = ({ user, token, orgTheme, isPlatformView = false }) => {
  const [activeView, setActiveView] = useState(isPlatformView ? 'platform' : 'queue');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Handle ?tab= query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'capacity') setActiveView('capacity');
    else if (tab === 'directory') setActiveView('profiles');
    else if (tab === 'platform') setActiveView('platform');
    else if (tab === 'dashboard') setActiveView('dashboard');
  }, []);

  const handleIntakeComplete = useCallback(() => {
    setActiveView('queue');
  }, []);

  // Fetch pending referral count for clinician inbox badge
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/api/nexus/my-referrals/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setPendingCount(data.count || 0);
      } catch {
        // Silently fail
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [API, token]);

  const isClinicianRole = ["therapist", "iic_therapist", "ba"].includes(user?.role);
  const isManagement = ["admin", "super_admin", "supervisor", "office_manager", "billing_admin"].includes(user?.role);
  const isPlatformAdmin = isPlatformView || ["super_admin", "platform_admin"].includes(user?.role);

  return (
    <div data-testid="nexus-hub" className={`space-y-4 ${isPlatformView ? 'max-w-7xl mx-auto px-4 sm:px-6' : ''}`}>
      {/* Back button for platform view */}
      {isPlatformView && (
        <button
          onClick={() => window.history.back()}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 -mb-2"
          data-testid="nexus-back-btn"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Platform Admin
        </button>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {isPlatformView ? 'CounselCore Nexus Network' : 'CounselCore Nexus'}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {isPlatformView 
              ? 'Cross-organization referral routing, capacity visibility & provider matching'
              : 'Referral routing & capacity management'}
          </p>
        </div>
      </div>

      <Tabs value={activeView} onValueChange={setActiveView} className="space-y-4">
        <TabsList className="bg-white border border-slate-200 shadow-sm p-1 rounded-lg flex-wrap">
          {isManagement && (
            <TabsTrigger
              value="queue"
              data-testid="nexus-queue-tab"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
            >
              <Inbox className="w-3.5 h-3.5 mr-1.5" />
              Referral Queue
            </TabsTrigger>
          )}
          <TabsTrigger
            value="inbox"
            data-testid="nexus-inbox-tab"
            className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium relative"
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" />
            My Referrals
            {pendingCount > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
          {isManagement && (
            <>
              <TabsTrigger
                value="capacity"
                data-testid="nexus-capacity-tab"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
              >
                <Users className="w-3.5 h-3.5 mr-1.5" />
                Capacity Board
              </TabsTrigger>
              <TabsTrigger
                value="intake"
                data-testid="nexus-intake-tab"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
              >
                <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
                New Referral
              </TabsTrigger>
              <TabsTrigger
                value="profiles"
                data-testid="nexus-profiles-tab"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
              >
                <UserCog className="w-3.5 h-3.5 mr-1.5" />
                Provider Directory
              </TabsTrigger>
              <TabsTrigger
                value="dashboard"
                data-testid="nexus-dashboard-tab"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                Dashboard
              </TabsTrigger>
            </>
          )}
          {isPlatformAdmin && (
            <TabsTrigger
              value="platform"
              data-testid="nexus-platform-tab"
              className="data-[state=active]:bg-violet-600 data-[state=active]:text-white text-slate-600 px-3 py-1.5 rounded-md text-xs font-medium"
            >
              <Globe className="w-3.5 h-3.5 mr-1.5" />
              Platform Routing
            </TabsTrigger>
          )}
        </TabsList>

        {isManagement && (
          <TabsContent value="queue">
            <NexusReferralQueue
              token={token}
              API={API}
              user={user}
              onSelectReferral={setSelectedReferral}
              selectedReferral={selectedReferral}
            />
          </TabsContent>
        )}

        <TabsContent value="inbox">
          <NexusClinicianInbox token={token} API={API} user={user} />
        </TabsContent>

        {isManagement && (
          <>
            <TabsContent value="capacity">
              <NexusCapacityBoard token={token} API={API} user={user} />
            </TabsContent>

            <TabsContent value="intake">
              <NexusReferralIntake
                token={token}
                API={API}
                user={user}
                onComplete={handleIntakeComplete}
              />
            </TabsContent>

            <TabsContent value="profiles">
              <NexusClinicianProfile token={token} API={API} user={user} />
            </TabsContent>

            <TabsContent value="dashboard">
              <NexusDashboard token={token} API={API} user={user} />
            </TabsContent>
          </>
        )}

        {isPlatformAdmin && (
          <TabsContent value="platform">
            <NexusPlatformView token={token} API={API} user={user} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default NexusHub;
